import { Head, router, usePage, Link } from "@inertiajs/react"
import { Hash, Search, SearchX, X } from "lucide-react"
import { useState } from "react"

import HashtagText from "@/components/feed/hashtag-text"
import PlayerCard from "@/components/players/player-card"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Input } from "@/components/ui/input"
import AppLayout from "@/layouts/app-layout"
import type { SearchIndex } from "@/types/serializers"

const POSITIONS = [
  { value: "portero", label: "Portero" },
  { value: "defensa_central", label: "Defensa Central" },
  { value: "lateral_derecho", label: "Lateral Derecho" },
  { value: "lateral_izquierdo", label: "Lateral Izquierdo" },
  { value: "mediocampista_central", label: "Mediocampista Central" },
  { value: "mediocampista_ofensivo", label: "Mediocampista Ofensivo" },
  { value: "mediocampista_defensivo", label: "Mediocampista Defensivo" },
  { value: "extremo_derecho", label: "Extremo Derecho" },
  { value: "extremo_izquierdo", label: "Extremo Izquierdo" },
  { value: "delantero_centro", label: "Delantero Centro" },
  { value: "segundo_delantero", label: "Segundo Delantero" },
]

const FEET = [
  { value: "derecho", label: "Derecho" },
  { value: "izquierdo", label: "Izquierdo" },
  { value: "ambidiestro", label: "Ambidiestro" },
]

type Filters = SearchIndex["filters"]

function FilterChip({
  label,
  active,
  onClick,
  onClear,
}: {
  label: string
  active: boolean
  onClick: () => void
  onClear?: () => void
}) {
  return (
    <button
      type="button"
      onClick={active && onClear ? onClear : onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold tracking-wider uppercase transition-colors ${
        active
          ? "border-krown-200 bg-krown-200/10 text-krown-50"
          : "border-krown-grey-65 text-muted-foreground hover:border-krown-grey-50 hover:text-krown-grey-30 bg-transparent"
      }`}
    >
      {label}
      {active && onClear && <X className="h-3 w-3" />}
    </button>
  )
}

export default function SearchIndex({
  players,
  pagination,
  filters,
  saved_player_ids,
  hashtag,
  hashtag_posts,
}: SearchIndex) {
  const page = usePage()
  const auth = page.props.auth as { user: { role: string } }
  const userRole = auth?.user?.role
  const [query, setQuery] = useState(filters.q ?? "")
  const [showPositions, setShowPositions] = useState(false)
  const [showFeet, setShowFeet] = useState(false)
  const [ageMin, setAgeMin] = useState(filters.age_min ?? "")
  const [ageMax, setAgeMax] = useState(filters.age_max ?? "")

  function applyFilters(newFilters: Partial<Filters> & { page?: string }) {
    const merged: Partial<Filters> & { page?: string } = {
      ...filters,
      ...newFilters,
    }
    // Reset page to 1 when any filter changes (not when paginating)
    if (!("page" in newFilters)) {
      delete merged.page
    }
    const params: Record<string, string> = {}
    for (const [key, value] of Object.entries(merged)) {
      if (value) params[key] = value
    }
    router.visit("/buscar", { data: params, preserveState: true })
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    applyFilters({ q: query })
  }

  function clearFilter(key: keyof Filters) {
    const newFilters = { ...filters }
    delete newFilters[key]
    const params: Record<string, string> = {}
    for (const [k, value] of Object.entries(newFilters)) {
      if (value) params[k] = value
    }
    router.visit("/buscar", { data: params, preserveState: true })
  }

  function clearFilters() {
    setQuery("")
    setAgeMin("")
    setAgeMax("")
    router.visit("/buscar", { preserveState: true })
  }

  function clearAgeFilter() {
    const newFilters = { ...filters }
    delete newFilters.age_min
    delete newFilters.age_max
    const params: Record<string, string> = {}
    for (const [k, value] of Object.entries(newFilters)) {
      if (value) params[k] = value
    }
    setAgeMin("")
    setAgeMax("")
    router.visit("/buscar", { data: params })
  }

  const hasActiveFilters =
    filters.position ??
    filters.country ??
    filters.foot ??
    filters.age_min ??
    filters.age_max

  const resultText =
    pagination.count === 1
      ? "1 jugador encontrado"
      : `${pagination.count} jugadores encontrados`

  const activePositionLabel = POSITIONS.find(
    (p) => p.value === filters.position,
  )?.label
  const activeFootLabel = FEET.find((f) => f.value === filters.foot)?.label
  return (
    <AppLayout>
      <Head title="Buscar jugadores | KrownFT" />

      <div className="bg-background min-h-svh">
        <div className="mx-auto max-w-6xl px-4 py-6">
          {hashtag && Object.keys(hashtag).length > 0 ? (
            <>
              {hashtag_posts && hashtag_posts.length > 0 && (
                <div className="mb-8">
                  <div className="mb-4 flex items-center gap-2">
                    <Hash className="text-krown-100 h-5 w-5" />
                    <h2 className="text-foreground text-lg font-bold">
                      {hashtag}
                    </h2>
                    <span className="text-muted-foreground text-sm">
                      {hashtag_posts.length} publicaciones
                    </span>
                  </div>
                  <div className="space-y-4">
                    {hashtag_posts.map((post) => (
                      <Link
                        key={post.id}
                        href={`/publicaciones/${post.id}/comentarios`}
                        className="border-border bg-card block rounded-lg border p-4"
                      >
                        <div className="mb-2 flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage
                              src={post.user.avatar_url ?? undefined}
                              alt={post.user.full_name}
                            />
                            <AvatarFallback className="bg-krown-200 text-xs font-bold text-white">
                              {post.user.first_name[0]}
                              {post.user.last_name[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-foreground text-sm font-semibold">
                              {post.user.full_name}
                            </p>
                            <p className="text-muted-foreground text-xs">
                              @{post.user.nickname}
                            </p>
                          </div>
                        </div>
                        {post.body && (
                          <p className="text-krown-grey-30 text-sm">
                            <HashtagText text={post.body} />
                          </p>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {hashtag && (!hashtag_posts || hashtag_posts.length === 0) && (
                <div className="mb-8 flex flex-col items-center py-8 text-center">
                  <Hash className="text-krown-grey-60 mb-3 h-12 w-12" />
                  <p className="text-krown-grey-50 text-lg font-semibold">
                    No hay publicaciones con #{hashtag}
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Search bar */}
              <form onSubmit={handleSearch} className="mb-6">
                <div className="relative">
                  <Search className="text-krown-grey-50 absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Buscar jugadores por nombre, ciudad, país..."
                    className="border-border bg-card placeholder:text-krown-grey-50 focus-visible:border-krown-200 focus-visible:ring-krown-200/30 h-12 rounded-lg pl-11 text-base text-white"
                  />
                </div>
              </form>

              {/* Filter chips */}
              <div className="mb-6 flex flex-wrap items-center gap-2">
                {/* Position filter */}
                <div className="relative">
                  <FilterChip
                    label={activePositionLabel ?? "Posición"}
                    active={!!filters.position}
                    onClick={() => setShowPositions(!showPositions)}
                    onClear={() => clearFilter("position")}
                  />
                  {showPositions && !filters.position && (
                    <div className="border-border bg-card absolute top-full left-0 z-50 mt-1 max-h-64 overflow-y-auto rounded-lg border py-1 shadow-lg">
                      {POSITIONS.map((p) => (
                        <button
                          key={p.value}
                          type="button"
                          onClick={() => {
                            applyFilters({ position: p.value })
                            setShowPositions(false)
                          }}
                          className="text-krown-grey-30 hover:bg-muted block w-full px-4 py-2 text-left text-sm hover:text-white"
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Foot filter */}
                <div className="relative">
                  <FilterChip
                    label={activeFootLabel ?? "Pie"}
                    active={!!filters.foot}
                    onClick={() => setShowFeet(!showFeet)}
                    onClear={() => clearFilter("foot")}
                  />
                  {showFeet && !filters.foot && (
                    <div className="border-border bg-card absolute top-full left-0 z-50 mt-1 overflow-y-auto rounded-lg border py-1 shadow-lg">
                      {FEET.map((f) => (
                        <button
                          key={f.value}
                          type="button"
                          onClick={() => {
                            applyFilters({ foot: f.value })
                            setShowFeet(false)
                          }}
                          className="text-krown-grey-30 hover:bg-muted block w-full px-4 py-2 text-left text-sm hover:text-white"
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Country filter chip */}
                {filters.country ? (
                  <FilterChip
                    label={filters.country}
                    active={true}
                    onClick={() => clearFilter("country")}
                    onClear={() => clearFilter("country")}
                  />
                ) : (
                  <input
                    type="text"
                    placeholder="País"
                    className="border-krown-grey-65 text-muted-foreground placeholder:text-muted-foreground focus:border-krown-200 focus:text-krown-50 inline-flex h-8 items-center rounded-full border bg-transparent px-3 text-xs font-semibold tracking-wider uppercase outline-none"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        applyFilters({ country: e.currentTarget.value })
                      }
                    }}
                  />
                )}

                {/* Age range */}
                {filters.age_min || filters.age_max ? (
                  <FilterChip
                    label={`${filters.age_min ?? "?"}-${filters.age_max ?? "?"} años`}
                    active={true}
                    onClick={clearAgeFilter}
                    onClear={clearAgeFilter}
                  />
                ) : (
                  <div className="inline-flex items-center gap-1">
                    <input
                      type="number"
                      placeholder="Min"
                      min={14}
                      max={50}
                      value={ageMin}
                      onChange={(e) => setAgeMin(e.target.value)}
                      className="border-krown-grey-65 text-muted-foreground placeholder:text-muted-foreground focus:border-krown-200 focus:text-krown-50 h-8 w-14 rounded-full border bg-transparent px-2 text-center text-xs font-semibold outline-none"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          applyFilters({
                            age_min: ageMin || undefined,
                            age_max: ageMax || undefined,
                          })
                        }
                      }}
                    />
                    <span className="text-krown-grey-50">-</span>
                    <input
                      type="number"
                      placeholder="Max"
                      min={14}
                      max={50}
                      value={ageMax}
                      onChange={(e) => setAgeMax(e.target.value)}
                      className="border-krown-grey-65 text-muted-foreground placeholder:text-muted-foreground focus:border-krown-200 focus:text-krown-50 h-8 w-14 rounded-full border bg-transparent px-2 text-center text-xs font-semibold outline-none"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          applyFilters({
                            age_min: ageMin || undefined,
                            age_max: ageMax || undefined,
                          })
                        }
                      }}
                    />
                  </div>
                )}

                {/* Clear all */}
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-muted-foreground text-xs font-semibold tracking-wider uppercase hover:text-white"
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>

              {/* Results count */}
              <p className="text-muted-foreground mb-4 text-sm">{resultText}</p>

              {/* Results grid */}
              {players.length > 0 ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {players.map((player) => (
                    <PlayerCard
                      key={player.id}
                      player={player}
                      userRole={userRole}
                      isSaved={saved_player_ids.includes(player.id)}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center py-20 text-center">
                  <SearchX className="text-krown-grey-60 mb-4 h-16 w-16" />
                  <p className="text-krown-grey-50 text-lg font-semibold">
                    Sin resultados
                  </p>
                  <p className="text-krown-grey-60 mt-1 text-sm">
                    No se encontraron jugadores con los filtros seleccionados.
                  </p>
                </div>
              )}

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="mt-8 flex justify-center gap-2">
                  {Array.from(
                    { length: pagination.pages },
                    (_, i) => i + 1,
                  ).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() =>
                        applyFilters({ ...filters, page: String(p) })
                      }
                      className={`h-9 w-9 rounded-full text-sm font-semibold transition-colors ${
                        p === pagination.page
                          ? "bg-krown-200 text-white"
                          : "border-krown-grey-65 text-muted-foreground hover:border-krown-grey-50 border hover:text-white"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
